import { supabase } from './supabase';

// Type definitions
export interface JoSAAFormData {
  name: string;
  email: string;
  jeeMainCRL: string;
  jeeAdvancedQualified: boolean;
  jeeAdvancedRank: string;
  category: 'General' | 'OBC-NCL' | 'EWS' | 'SC' | 'ST';
  gender: 'Male' | 'Female';
  homeState: string;
  isPwD?: boolean;
}

export interface JoSAAInstitute {
  id: string;
  name: string;
  short_name: string;
  institute_type: 'IIT' | 'NIT' | 'IIIT' | 'GFTI';
  location: string;
  state: string;
  established?: string;
  website?: string;
  nirf_ranking?: number;
  image_url: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  branches?: JoSAABranch[];
}

export interface JoSAABranch {
  id: string;
  institute_id: string;
  branch_name: string;
  branch_code: string;
  degree_type: string;
  duration: string;
  seats_total: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  cutoffs?: JoSAACutoff[];
}

export interface JoSAACutoff {
  id: string;
  branch_id: string;
  year: string;
  round_number: number;
  round_name: string;
  quota: 'All India' | 'Home State' | 'Other State';
  seat_type: 'Gender-Neutral' | 'Female-only (including Supernumerary)' | 'Supernumerary';
  category: 'General' | 'OBC-NCL' | 'EWS' | 'SC' | 'ST';
  opening_rank?: number;
  closing_rank: number;
  seats_available: number;
  seats_filled: number;
  created_at: string;
  updated_at: string;
}

export interface JoSAAPrediction {
  institute: JoSAAInstitute;
  branch: JoSAABranch;
  cutoff: JoSAACutoff;
  admissionChance: 'High' | 'Medium' | 'Low';
  probabilityPercentage: number;
  rankDifference: number;
  userRank: number;
  examType: 'JEE Main' | 'JEE Advanced';
}

// Calculate admission probability based on rank difference
const calculateAdmissionProbability = (userRank: number, closingRank: number): { chance: 'High' | 'Medium' | 'Low', percentage: number } => {
  const rankDifference = closingRank - userRank;
  const percentageDifference = (rankDifference / closingRank) * 100;

  if (userRank <= closingRank * 0.7) {
    return { chance: 'High', percentage: Math.min(95, 85 + Math.abs(percentageDifference) / 2) };
  } else if (userRank <= closingRank * 0.9) {
    return { chance: 'High', percentage: Math.max(70, 80 - Math.abs(percentageDifference)) };
  } else if (userRank <= closingRank) {
    return { chance: 'Medium', percentage: Math.max(40, 70 - Math.abs(percentageDifference) * 1.5) };
  } else if (userRank <= closingRank * 1.1) {
    return { chance: 'Low', percentage: Math.max(20, 50 - Math.abs(percentageDifference)) };
  } else if (userRank <= closingRank * 1.3) {
    return { chance: 'Low', percentage: Math.max(10, 30 - Math.abs(percentageDifference) / 2) };
  } else {
    return { chance: 'Low', percentage: Math.max(5, 15 - Math.abs(percentageDifference) / 3) };
  }
};

// Find the most relevant cutoff round for a user's rank
const findMostRelevantRound = (userRank: number, cutoffs: JoSAACutoff[]): JoSAACutoff | null => {
  if (!cutoffs || cutoffs.length === 0) return null;

  // Sort cutoffs by year (descending) and round number (ascending) to prioritize recent data
  const sortedCutoffs = cutoffs.sort((a, b) => {
    if (a.year !== b.year) return b.year.localeCompare(a.year);
    return a.round_number - b.round_number;
  });

  // Find the best round where user can get admission
  let bestRound: JoSAACutoff | null = null;
  let bestRoundForUser: JoSAACutoff | null = null;

  for (const cutoff of sortedCutoffs) {
    // Track the overall best (lowest) cutoff
    if (!bestRound || cutoff.closing_rank < bestRound.closing_rank) {
      bestRound = cutoff;
    }

    // Find the best round where user's rank is within cutoff
    if (userRank <= cutoff.closing_rank) {
      if (!bestRoundForUser || cutoff.closing_rank < bestRoundForUser.closing_rank) {
        bestRoundForUser = cutoff;
      }
    }
  }

  // Return the round where user can get admission, or the best available round
  return bestRoundForUser || bestRound;
};

export const josaaService = {
  // Prediction generation
  generatePredictions: async (formData: JoSAAFormData): Promise<JoSAAPrediction[]> => {
    try {
      const jeeMainRank = parseInt(formData.jeeMainCRL);
      const jeeAdvancedRank = formData.jeeAdvancedQualified ? parseInt(formData.jeeAdvancedRank) : null;
      
      const predictions: JoSAAPrediction[] = [];
      
      // Get all institutes with branches and cutoffs
      const { data: institutes, error } = await supabase
        .from('josaa_institutes')
        .select(`
          *,
          branches:josaa_branches(
            *,
            cutoffs:josaa_cutoffs(*)
          )
        `)
        .eq('is_active', true)
        .order('nirf_ranking', { ascending: true, nullsLast: true });

      if (error) throw error;

      for (const institute of institutes || []) {
        // Skip IITs if JEE Advanced not qualified
        if (institute.institute_type === 'IIT' && !formData.jeeAdvancedQualified) {
          continue;
        }

        // Determine which rank to use
        const userRank = institute.institute_type === 'IIT' && jeeAdvancedRank 
          ? jeeAdvancedRank 
          : jeeMainRank;
        
        const examType = institute.institute_type === 'IIT' ? 'JEE Advanced' : 'JEE Main';

        for (const branch of institute.branches || []) {
          if (!branch.is_active) continue;

          // Filter cutoffs by category and quota
          const relevantCutoffs = (branch.cutoffs || []).filter(cutoff => {
            // Filter by category
            if (cutoff.category !== formData.category) return false;
            
            // Filter by quota (prefer home state if available)
            const isHomeState = institute.state === formData.homeState;
            if (isHomeState && cutoff.quota === 'Home State') return true;
            if (cutoff.quota === 'All India') return true;
            
            return false;
          });

          // Find the most relevant cutoff round for this user's rank
          const bestCutoff = findMostRelevantRound(userRank, relevantCutoffs);

          if (!bestCutoff) continue;

          // Check if user has a chance (rank should be close to cutoff)
          if (userRank > bestCutoff.closing_rank * 1.5) continue;

          // Calculate admission probability
          const { chance, percentage } = calculateAdmissionProbability(userRank, bestCutoff.closing_rank);
          const rankDifference = bestCutoff.closing_rank - userRank;

          predictions.push({
            institute,
            branch,
            cutoff: bestCutoff,
            admissionChance: chance,
            probabilityPercentage: Math.round(percentage),
            rankDifference,
            userRank,
            examType
          });
        }
      }

      // Sort predictions by cutoff rank (ascending) - best chances first
      predictions.sort((a, b) => {
        // Primary sort: by closing rank (ascending) - lower cutoff = better chance
        if (a.cutoff.closing_rank !== b.cutoff.closing_rank) {
          return a.cutoff.closing_rank - b.cutoff.closing_rank;
        }
        
        // Secondary sort: by NIRF ranking (ascending) - better ranked institutes first
        const aNirf = a.institute.nirf_ranking || 999999;
        const bNirf = b.institute.nirf_ranking || 999999;
        if (aNirf !== bNirf) {
          return aNirf - bNirf;
        }
        
        // Tertiary sort: by institute type priority (IIT > NIT > IIIT > GFTI)
        const typeOrder = { 'IIT': 1, 'NIT': 2, 'IIIT': 3, 'GFTI': 4 };
        const aTypeOrder = typeOrder[a.institute.institute_type as keyof typeof typeOrder] || 5;
        const bTypeOrder = typeOrder[b.institute.institute_type as keyof typeof typeOrder] || 5;
        
        return aTypeOrder - bTypeOrder;
      });

      return predictions.slice(0, 100); // Limit to top 100 predictions
    } catch (error) {
      console.error('Error generating JoSAA predictions:', error);
      throw error;
    }
  },

  // Institute CRUD operations
  getInstitutes: async (filters?: { type?: string; state?: string; search?: string }) => {
    try {
      let query = supabase
        .from('josaa_institutes')
        .select(`
          *,
          branches:josaa_branches(
            *,
            cutoffs:josaa_cutoffs(*)
          )
        `)
        .eq('is_active', true)
        .order('name');

      if (filters?.type && filters.type !== 'all') {
        query = query.eq('institute_type', filters.type);
      }

      if (filters?.state && filters.state !== 'all') {
        query = query.eq('state', filters.state);
      }

      if (filters?.search) {
        query = query.or(`name.ilike.%${filters.search}%,short_name.ilike.%${filters.search}%,location.ilike.%${filters.search}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching institutes:', error);
      throw error;
    }
  },

  getInstituteById: async (id: string) => {
    try {
      const { data, error } = await supabase
        .from('josaa_institutes')
        .select(`
          *,
          branches:josaa_branches(
            *,
            cutoffs:josaa_cutoffs(*)
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching institute:', error);
      throw error;
    }
  },

  createInstitute: async (institute: Omit<JoSAAInstitute, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error } = await supabase
        .from('josaa_institutes')
        .insert(institute)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating institute:', error);
      throw error;
    }
  },

  updateInstitute: async (id: string, institute: Partial<JoSAAInstitute>) => {
    try {
      const { data, error } = await supabase
        .from('josaa_institutes')
        .update({ ...institute, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating institute:', error);
      throw error;
    }
  },

  deleteInstitute: async (id: string) => {
    try {
      const { error } = await supabase
        .from('josaa_institutes')
        .delete()
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting institute:', error);
      throw error;
    }
  },

  // Branch CRUD operations
  createBranch: async (branch: Omit<JoSAABranch, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error } = await supabase
        .from('josaa_branches')
        .insert(branch)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating branch:', error);
      throw error;
    }
  },

  updateBranch: async (id: string, branch: Partial<JoSAABranch>) => {
    try {
      const { data, error } = await supabase
        .from('josaa_branches')
        .update({ ...branch, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating branch:', error);
      throw error;
    }
  },

  deleteBranch: async (id: string) => {
    try {
      const { error } = await supabase
        .from('josaa_branches')
        .delete()
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting branch:', error);
      throw error;
    }
  },

  // Cutoff CRUD operations
  createCutoff: async (cutoff: Omit<JoSAACutoff, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error } = await supabase
        .from('josaa_cutoffs')
        .insert(cutoff)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating cutoff:', error);
      throw error;
    }
  },

  updateCutoff: async (id: string, cutoff: Partial<JoSAACutoff>) => {
    try {
      const { data, error } = await supabase
        .from('josaa_cutoffs')
        .update({ ...cutoff, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating cutoff:', error);
      throw error;
    }
  },

  deleteCutoff: async (id: string) => {
    try {
      const { error } = await supabase
        .from('josaa_cutoffs')
        .delete()
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting cutoff:', error);
      throw error;
    }
  },

  // Bulk operations for data import
  bulkCreateCutoffs: async (cutoffs: Omit<JoSAACutoff, 'id' | 'created_at' | 'updated_at'>[]) => {
    try {
      const { data, error } = await supabase
        .from('josaa_cutoffs')
        .insert(cutoffs)
        .select();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error bulk creating cutoffs:', error);
      throw error;
    }
  },

  // Analytics and statistics
  getStatistics: async () => {
    try {
      const [institutesResult, branchesResult, cutoffsResult] = await Promise.all([
        supabase.from('josaa_institutes').select('institute_type', { count: 'exact' }),
        supabase.from('josaa_branches').select('id', { count: 'exact' }),
        supabase.from('josaa_cutoffs').select('year', { count: 'exact' })
      ]);

      const institutesByType = institutesResult.data?.reduce((acc: any, inst: any) => {
        acc[inst.institute_type] = (acc[inst.institute_type] || 0) + 1;
        return acc;
      }, {}) || {};

      return {
        totalInstitutes: institutesResult.count || 0,
        totalBranches: branchesResult.count || 0,
        totalCutoffs: cutoffsResult.count || 0,
        institutesByType
      };
    } catch (error) {
      console.error('Error fetching statistics:', error);
      throw error;
    }
  }
};